<template>
  <div>
    <deal-details
      v-if="dataLoaded"
      ref="dealDetails"
      :is-unit-targeting-enabled="true"
      :is-audit-trail-enabled="true"
      :is-daypart-enabled="exchangeSettings.uiEnablePackageDaypart"
      :is-ad-duration-enabled="exchangeSettings.uiEnablePackageAdDuration"
      :is-exchange="true"
      :deal="deal"
      :locations="locations"
      :excluded-locations="excludedLocations"
      :sites="sites"
      :excluded-sites="excludedSites"
      :units="units"
      :excluded-units="excludedUnits"
      :markets="markets"
      :geofence-groups="geofenceGroups"
      :excluded-geofence-groups="excludedGeofenceGroups"
      :line-items="lineItems"
      :targeting-criteria="targetingCriteria"
      :audit-trail="auditTrail"
      :suppliers="suppliers"
      @get-audit-trail="handleGetAuditTrail"
      @publish-package="handlePublishPackage"
      @unpublish-package="handleUnpublishPackage"
      @retire-package="handleRetirePackage"
      @convert-to-line-item="handleConvertToLineitem"
      @previous-revision="handlePreviousRevision"
      @next-revision="handleNextRevision" />
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex';

  import DealDetails from '@/common/components/deals/DealDetails';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import ErrorHandler from '@/ErrorHandler';
  import { useMediaOwners, useTargetingCriteria } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  export default {
    name: 'PackageDetails',
    components: { DealDetails },
    data() {
      return {
        auditTrail: null,
        deal: {},
        dataLoaded: false,
        mediaOwnersQuery: useMediaOwners(),
        targetingCriteriaQuery: useTargetingCriteria(),
      };
    },
    computed: {
      ...mapState('exchApp', {
        exchangeSettings: (state) => state.exchangeSettings,
        excludedLocations: (state) => state.locationsExcluded,
        excludedSites: (state) => state.sitesExcluded,
        excludedUnits: (state) => state.unitsExcluded,
        geofenceGroups: (state) => state.geofenceGroups,
        excludedGeofenceGroups: (state) => state.geofenceGroupsExcluded,
        lineItems: (state) => state.lineItems,
        locations: (state) => state.locations,
        markets: (state) => state.markets,
        sites: (state) => state.sites,
        targetingCriteria: (state) => state.targetingCriteria,
        units: (state) => state.units,
      }),
      suppliers() {
        return this.mediaOwnersQuery.items || [];
      },
      targetingCriteria() {
        return this.targetingCriteriaQuery.items || [];
      },
    },
    async beforeMount() {
      this.dataLoaded = false;
      await Promise.all([
        this.loadPackageDetails(),
        waitForPendingQueries([this.mediaOwnersQuery, this.targetingCriteriaQuery]),
      ]).catch((err) => {
        ErrorHandler.handleExceptionInUIComponent(err);
      });
      this.dataLoaded = true;
    },
    methods: {
      ...mapActions(['updatePackageState']),
      ...mapActions('exchApp', [
        'getGeofenceGroups',
        'getLineItems',
        'getLocations',
        'getMarkets',
        'getPackage',
        'getPackageAudit',
        'getPackages',
        'getSites',
        'getUnits',
        'putPackageState',
        'savePackage',
      ]),
      async loadLocations(exclusion = false) {
        const locationIDs = exclusion ? this.deal.locationExclusionIDs : this.deal.locationTargetIDs;

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('location_id', locationIDs, FilterComparisonOperator.IN)]),
        });
        await this.getLocations({ queryParams, exclusion });
      },
      async loadSites(exclusion = false) {
        const siteIDs = exclusion ? this.deal.siteExclusionIDs : this.deal.siteIDs;

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('site_id', siteIDs, FilterComparisonOperator.IN)]),
        });
        await this.getSites({ queryParams, exclusion });
      },
      async loadUnits(exclusion = false) {
        const unitIDs = exclusion ? this.deal.unitExclusionIDs : this.deal.unitIDs;

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('unit_id', unitIDs, FilterComparisonOperator.IN)]),
        });
        await this.getUnits({ queryParams, exclusion });
      },
      async loadGeofenceGroups(exclusion = false) {
        const geofenceIDs = exclusion ? this.deal.geofenceGroupExclusionIDs : this.deal.geofenceGroupIDs;

        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([
            new Filter('geofencegroup_id', geofenceIDs, FilterComparisonOperator.IN),
          ]),
        });
        // await queryParams.run(this.getGeofenceGroups);
        await this.getGeofenceGroups({ queryParams, exclusion });
      },
      async loadMarkets() {
        const queryParams = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([
            new Filter('market_id', this.deal.marketIDs, FilterComparisonOperator.IN),
          ]),
          include: ['market_id', 'name', 'type', 'external_id', 'location_id'],
        });
        await queryParams.run(this.getMarkets);
      },

      async loadPackageLineItems() {
        const queryParams = QueryParams.for({
          top: null,
          sort: 'created_on_utc desc',
          filterCollection: new FilterCollection([
            new Filter('package.deal_id', [this.deal.dealID], FilterComparisonOperator.EQUAL),
          ]),
          expand: ['package'],
          showLoader: false,
        });
        await queryParams.run(this.getLineItems);
      },
      async loadPackageDetails() {
        this.deal = await this.getPackage({ id: this.$route.params.id, showLoader: false });

        await Promise.all([
          this.loadPackageLineItems(),
          this.deal.locationTargetIDs.length > 0 ? this.loadLocations() : null,
          this.deal.locationExclusionIDs.length > 0 ? this.loadLocations(true) : null,
          this.deal.siteIDs.length > 0 ? this.loadSites() : null,
          this.deal.siteExclusionIDs.length > 0 ? this.loadSites(true) : null,
          this.deal.unitIDs.length > 0 ? this.loadUnits() : null,
          this.deal.unitExclusionIDs.length > 0 ? this.loadUnits(true) : null,
          this.deal.marketIDs.length > 0 ? this.loadMarkets() : null,
          this.deal.geofenceGroupIDs.length > 0 ? this.loadGeofenceGroups() : null,
          this.deal.geofenceGroupExclusionIDs.length > 0 ? this.loadGeofenceGroups(true) : null,
        ]);
      },
      async reload() {
        this.dataLoaded = false;
        await this.loadPackageDetails().catch((err) => {
          UserNotifications.somethingWentWrong(err, i18n.global.t('messages.failedToGetPackage'));
          throw err;
        });
        this.dataLoaded = true;
      },
      async handlePublishPackage(pack) {
        await this.putPackageState({ instance: pack });
        this.$refs.dealDetails.closeModal();
      },
      async handleUnpublishPackage(pack) {
        await this.putPackageState({ instance: pack });
        this.$refs.dealDetails.closeModal();
      },
      async handleRetirePackage(pack) {
        await this.putPackageState({ instance: pack });
        this.$refs.dealDetails.closeModal();
      },
      async handleGetAuditTrail() {
        this.auditTrail = await this.getPackageAudit({ id: this.deal.id });
      },
      async handleConvertToLineitem(/* pack */) {
        // TODO: handle this properly when we know what we want to do on the backend.
        // const now = DateTime.local();
        // if (pack.state !== PackageStatus.PUBLISHED) {
        //   this.updatePackageState({
        //     package: this.deal,
        //     value: PackageStatus.PUBLISHED,
        //   });
        //   await this.putPackageState({ instance: pack });
        // }
        //
        //
        // await this.convertDealToLineitems(pack);
        // UserNotifications.success({
        //   title: capitalize(i18n.global.t('names.success', { n: 1 })),
        //   message: i18n.global.t('messages.packageConvertedToLineitemSuccessfully'),
        // });
        // this.$refs.dealDetails.closeModal();
        // await this.loadPackageLineItems();
      },
      async handlePreviousRevision() {
        const queryParams = QueryParams.for({
          filterCollection: new FilterCollection([
            new Filter('deal_id', [this.deal.dealID]),
            new Filter('revision', [this.deal.revision - 1]),
          ]),
        });
        const packages = await queryParams.run(this.getPackages);
        if (packages.items.length > 0) {
          this.$router
            .push({ name: 'deal-details', params: { id: packages.items[0].id } }, () => {
              this.reload();
            })
            .catch(() => {
              // Do nothing
            });
        }
      },
      handleNextRevision() {
        this.$router
          .push({ name: 'deal-details', params: { id: this.deal.revisedByID } }, () => {
            this.reload();
          })
          .catch(() => {
            // Do nothing
          });
      },
    },
  };
</script>

<style scoped></style>
